import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Home, ArrowRight, CheckCircle2, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "wouter";
import LeadFormModal from "./LeadFormModal";
import { useState } from "react";

interface Project {
  id: string;
  location: string;
  title: string;
  type: string;
  typeIcon: "bungalow" | "duplex";
  year: string;
  description: string;
  features: string[];
  images: { src: string; alt: string; caption: string }[];
  cta: string;
}

const projects: Project[] = [
  {
    id: "lake-bonavista",
    location: "Lake Bonavista, Calgary",
    title: "1969 Bungalow Complete Transformation",
    type: "Bungalow",
    typeIcon: "bungalow",
    year: "Built 1969",
    description: "This 1969 bungalow received a <strong>complete overhaul</strong>—new everything. We transformed an outdated home into a stunning modern residence while preserving the character of the Lake Bonavista neighborhood.",
    features: [
      "New roof & exterior",
      "Open concept layout",
      "Modern kitchen",
      "New flooring throughout",
      "Skylights added",
      "Updated electrical & plumbing"
    ],
    images: [
      { src: "/lake-bonavista-exterior.jpg", alt: "Lake Bonavista bungalow exterior after complete renovation", caption: "Exterior - Modern curb appeal" },
      { src: "/lake-bonavista-interior-1.jpg", alt: "Lake Bonavista open concept living room and kitchen", caption: "Open concept living & kitchen" },
      { src: "/lake-bonavista-interior-2.jpg", alt: "Lake Bonavista living room with vaulted ceilings", caption: "Living room with skylights" }
    ],
    cta: "Have a similar home? We're actively looking for 1960s-1980s bungalows in established Calgary neighborhoods."
  },
  {
    id: "tuxedo-park",
    location: "Tuxedo Park, Calgary",
    title: "1950s Bungalow to Luxury Duplex",
    type: "New Build Duplex",
    typeIcon: "duplex",
    year: "1950s Teardown",
    description: "We purchased and demolished a dated 1950s bungalow to build this <strong>stunning luxury duplex</strong>. Each side features 1,985 sq ft of high-end finishes, modern design, and premium craftsmanship.",
    features: [
      "1,985 sq ft per side",
      "High-end finishes throughout",
      "Gourmet kitchen with quartz",
      "Open concept living",
      "Modern fireplace feature",
      "Premium hardwood floors"
    ],
    images: [
      { src: "/tuxedo-park-exterior.jpg", alt: "Tuxedo Park luxury duplex exterior with brick facade", caption: "Exterior - Brick & modern design" },
      { src: "/tuxedo-park-living.jpg", alt: "Tuxedo Park duplex living room with fireplace", caption: "Living room with fireplace" },
      { src: "/tuxedo-park-kitchen.jpg", alt: "Tuxedo Park duplex gourmet kitchen", caption: "Gourmet kitchen" }
    ],
    cta: "Have an older home on a good lot? We're looking for teardown opportunities in inner-city Calgary."
  },
  {
    id: "southwood",
    location: "Southwood, Calgary",
    title: "1965 Bungalow + Basement Suite Addition",
    type: "Renovation + Suite",
    typeIcon: "bungalow",
    year: "Built 1965",
    description: "We transformed this 1965 bungalow into a <strong>modern home with income potential</strong>. Added a double garage and developed a legal 2-bedroom basement suite—perfect for mortgage helpers or rental income.",
    features: [
      "Double garage added",
      "2-bed basement suite",
      "New flooring throughout",
      "All new windows",
      "Updated kitchens & baths",
      "Modern finishes"
    ],
    images: [
      { src: "/southwood-exterior.jpg", alt: "Southwood bungalow exterior after renovation", caption: "Exterior - Fresh curb appeal" },
      { src: "/southwood-kitchen.jpg", alt: "Southwood main floor open kitchen and dining", caption: "Main floor kitchen & dining" },
      { src: "/southwood-basement.jpg", alt: "Southwood legal basement suite kitchen", caption: "Basement suite kitchen" }
    ],
    cta: "Have a bungalow with suite potential? We're looking for homes where we can add value through smart renovations."
  },
  {
    id: "willow-park",
    location: "Willow Park, Calgary",
    title: "1965 Bungalow Complete Gut Renovation",
    type: "Full Renovation",
    typeIcon: "bungalow",
    year: "Built 1965",
    description: "This 1965 bungalow was <strong>completely gutted and rebuilt from the inside out</strong>. We added dramatic vaulted ceilings with skylights, creating a bright, modern open-concept living space while keeping the original footprint.",
    features: [
      "Vaulted ceilings added",
      "Skylights throughout",
      "Complete gut renovation",
      "Modern open concept",
      "New exterior finishes",
      "Designer kitchen"
    ],
    images: [
      { src: "/willow-park-exterior.jpg", alt: "Willow Park bungalow modern exterior after renovation", caption: "Exterior - Modern transformation" },
      { src: "/willow-park-kitchen.jpg", alt: "Willow Park open kitchen with vaulted ceilings and skylights", caption: "Kitchen with vaulted ceilings" },
      { src: "/willow-park-bedroom.jpg", alt: "Willow Park renovated bedroom with modern finishes", caption: "Bright, modern bedroom" }
    ],
    cta: "Have a dated bungalow? We specialize in complete transformations that unlock a home's full potential."
  },
  {
    id: "west-hillhurst",
    location: "West Hillhurst, Calgary",
    title: "1950s Bungalow to 2 Luxury Single-Family Homes",
    type: "New Build Singles",
    typeIcon: "duplex",
    year: "1950s Teardown",
    description: "We purchased and demolished a dated 1950s bungalow to build <strong>two stunning single-family homes</strong>. Each home features high-end finishes, open-concept living, and modern design in one of Calgary's most desirable inner-city neighborhoods.",
    features: [
      "2 single-family homes",
      "High-end finishes",
      "Glass & wood staircase",
      "Gourmet kitchen",
      "Premium hardwood floors",
      "Modern open concept"
    ],
    images: [
      { src: "/west-hillhurst-exterior.jpg", alt: "West Hillhurst new build single family homes exterior", caption: "Exterior - Modern architecture" },
      { src: "/west-hillhurst-kitchen.jpg", alt: "West Hillhurst luxury kitchen with island and glass staircase", caption: "Kitchen with glass staircase" },
      { src: "/west-hillhurst-dining.jpg", alt: "West Hillhurst open concept dining and kitchen", caption: "Open concept dining" }
    ],
    cta: "Have an older home on a good inner-city lot? We're looking for redevelopment opportunities in established neighborhoods."
  }
];

export default function RecentWins() {
  const [currentProject, setCurrentProject] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  
  const project = projects[currentProject];

  const nextProject = () => {
    setCurrentProject((prev) => (prev + 1) % projects.length);
    setSelectedImage(0);
  };

  const prevProject = () => {
    setCurrentProject((prev) => (prev - 1 + projects.length) % projects.length);
    setSelectedImage(0);
  };

  return (
    <section className="py-20 bg-white border-t border-[oklch(0.9_0.02_85)]">
      <div className="container">
        <div className="text-center mb-12">
          <Badge className="bg-[oklch(0.92_0.05_145)] text-[oklch(0.35_0.12_145)] hover:bg-[oklch(0.92_0.05_145)] mb-4">
            Featured Projects
          </Badge>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-[oklch(0.2_0.03_145)] mb-4">
            See What We Do With Homes Like Yours
          </h2>
          <p className="text-lg text-[oklch(0.4_0.03_145)] max-w-2xl mx-auto">
            We don't just buy houses—we transform them. Here are recent projects that show the quality of our work.
          </p>
        </div>

        {/* Project Navigation */}
        <div className="flex justify-center items-center gap-4 mb-8">
          <button
            onClick={prevProject}
            className="p-2 rounded-full bg-[oklch(0.95_0.02_85)] hover:bg-[oklch(0.9_0.02_85)] transition-colors"
            aria-label="Previous project"
          >
            <ChevronLeft className="w-5 h-5 text-[oklch(0.35_0.12_145)]" />
          </button>
          <div className="flex gap-2">
            {projects.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setCurrentProject(idx); setSelectedImage(0); }}
                className={`w-3 h-3 rounded-full transition-all ${
                  currentProject === idx 
                    ? "bg-[oklch(0.35_0.12_145)] w-8" 
                    : "bg-[oklch(0.85_0.02_85)] hover:bg-[oklch(0.75_0.02_85)]"
                }`}
                aria-label={`View project ${idx + 1}`}
              />
            ))}
          </div>
          <button
            onClick={nextProject}
            className="p-2 rounded-full bg-[oklch(0.95_0.02_85)] hover:bg-[oklch(0.9_0.02_85)] transition-colors"
            aria-label="Next project"
          >
            <ChevronRight className="w-5 h-5 text-[oklch(0.35_0.12_145)]" />
          </button>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden shadow-xl">
                <img 
                  src={project.images[selectedImage].src}
                  alt={project.images[selectedImage].alt}
                  className="w-full h-full object-cover transition-opacity duration-300"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-white text-sm font-medium">{project.images[selectedImage].caption}</p>
                </div>
              </div>
              
              {/* Thumbnail Navigation */}
              <div className="grid grid-cols-3 gap-3">
                {project.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`relative aspect-[4/3] rounded-lg overflow-hidden transition-all ${
                      selectedImage === idx 
                        ? "ring-2 ring-[oklch(0.75_0.15_85)] ring-offset-2" 
                        : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img 
                      src={img.src}
                      alt={img.alt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[oklch(0.35_0.12_145)]">
                  <MapPin className="w-5 h-5" />
                  <span className="font-medium">{project.location}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif font-bold text-[oklch(0.2_0.03_145)]">
                  {project.title}
                </h3>
              </div>

              <div className="flex flex-wrap gap-3">
                <Badge variant="outline" className="border-[oklch(0.75_0.15_85)] text-[oklch(0.35_0.12_145)]">
                  <Calendar className="w-3 h-3 mr-1" />
                  {project.year}
                </Badge>
                <Badge variant="outline" className="border-[oklch(0.75_0.15_85)] text-[oklch(0.35_0.12_145)]">
                  {project.typeIcon === "duplex" ? (
                    <Building2 className="w-3 h-3 mr-1" />
                  ) : (
                    <Home className="w-3 h-3 mr-1" />
                  )}
                  {project.type}
                </Badge>
                <Badge className="bg-[oklch(0.35_0.12_145)] text-white">
                  Completed
                </Badge>
              </div>

              <p 
                className="text-[oklch(0.4_0.03_145)] leading-relaxed"
                dangerouslySetInnerHTML={{ __html: project.description }}
              />

              <div className="bg-[oklch(0.97_0.02_85)] rounded-xl p-6 space-y-4">
                <h4 className="font-bold text-[oklch(0.2_0.03_145)]">What We Did:</h4>
                <div className="grid grid-cols-2 gap-3">
                  {project.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-[oklch(0.4_0.03_145)]">
                      <CheckCircle2 className="w-4 h-4 text-[oklch(0.35_0.12_145)] shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[oklch(0.25_0.1_145)] rounded-xl p-6 text-white space-y-4">
                <p className="text-[oklch(0.9_0.02_85)]">
                  <strong className="text-white">{project.cta}</strong> Get an unconditional offer today.
                </p>
                <LeadFormModal 
                  source={`featured-project-${project.id}`}
                  trigger={
                    <Button className="w-full bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] font-bold py-3 h-auto group">
                      Get Your Offer
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* See More Projects Button */}
        <div className="text-center mt-12">
          <Link href="/projects">
            <Button variant="outline" className="border-[oklch(0.35_0.12_145)] text-[oklch(0.35_0.12_145)] hover:bg-[oklch(0.35_0.12_145)] hover:text-white px-8 py-3 h-auto font-semibold group">
              See All Projects
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          <p className="text-sm text-[oklch(0.5_0.03_145)] mt-3">500+ projects completed across Calgary</p>
        </div>
      </div>
    </section>
  );
}
